// Placeholder for tempoissues/components/common/issues/issue-context-menu.tsx
// Please replace this with the actual content from your tempoissues project.
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuCheckboxItem,
  ContextMenu,
} from "@/components/ui/context-menu";
import {
  CircleCheck,
  User as UserIcon,
  Tag,
  Trash2,
  ChevronLeft,
  CircleUserRound,
  CheckIcon,
  Plus,
  CalendarClock,
  ClockFading,
  CircleDashed,
  TriangleDashed,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import * as React from "react";
import { useState, useEffect, useRef, useMemo } from "react";
import { useIssuesStore } from "@/store/issues-store";
import { toast } from "sonner";
import { User } from "@/types/users";
import { LabelInterface } from "@/types/labels";
import { Priority, NoPriorityIcon } from "@/types/priorities";
import { Status } from "@/types/status";
import { Issue } from "@/types/issues";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { useSelector } from "react-redux";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useSearchParams } from "react-router";
import { useSession } from "@clerk/clerk-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDispatch } from "react-redux";
import { ContextMenuPortal } from "@/components/ui/context-menu";
import { setCanvasPreviews } from "@/store/supabaseSlice";
import { CanvasPreview } from "@/types/issues";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { HalfFilledTriangleIcon } from "@/assets/icons/half-filled-triangle-icon";
import { getPriorityName, defaultPriorities } from "@/types/priorities";
import { ESTIMATE_POINTS, ESTIMATE_TO_TSHIRT_SIZE } from "@/types/issues";
import { Calendar } from "@/components/ui/calendar";
import { format, isToday, isTomorrow } from "date-fns";
import { ShortcutHandler } from "@/components/providers/ShortcutProvider";
import { COLOR_LIST } from "@/utils/constants";
import { getAppliedStatusIconForIssues } from "@/utils/utils";
import { useIssueSelectionStore } from "@/store/zustand/issueSelectionStore";

interface IssueContextMenuProps {
  issueIds: string[];
  users: User[];
  labels: LabelInterface[];
  priorities?: Priority[];
  status?: Status[];
  projects?: any[];
}

export function IssueContextMenu({
  users = [],
  labels = [],
  priorities = defaultPriorities,
  status = [],
  projects = [],
  issueIds = [],
}: IssueContextMenuProps) {
  // Local UI state
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [labelView, setLabelView] = useState<"labels" | "new" | "colorSelect">(
    "labels"
  );
  const [newLabelName, setNewLabelName] = useState<string>("");
  const [labelSearch, setLabelSearch] = useState<string>("");
  const labelInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showDeleteCanvasModal, setShowDeleteCanvasModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [estimateSearch, setEstimateSearch] = useState<string>("");
  const estimateInputRef = useRef<HTMLInputElement>(null);
  const [prioritySearch, setPrioritySearch] = useState<string>("");
  const priorityInputRef = useRef<HTMLInputElement>(null);
  const [statusSearch, setStatusSearch] = useState<string>("");
  const statusInputRef = useRef<HTMLInputElement>(null);
  const [assigneeSearch, setAssigneeSearch] = useState<string>("");
  const assigneeInputRef = useRef<HTMLInputElement>(null);

  const issues = useIssuesStore((state) =>
    issueIds.map((id) => state.getIssueById(id))
  );
  const issueLabelIds = useMemo(() => {
    return issues.map((issue) => (issue?.labels as unknown as string[]) ?? []);
  }, [issues.map((issue) => issue?.labels as unknown as string[])]);

  // Sort and filter labels
  const sortedAndFilteredLabels = useMemo<{
    applied: LabelInterface[];
    available: LabelInterface[];
  }>(() => {
    const filtered = labels.filter(
      (label) =>
        label.hasOwnProperty("label") &&
        label.label?.toLowerCase().includes(labelSearch.toLowerCase())
    );

    // Split into applied and unapplied labels
    const applied = filtered
      .filter((label) => issueLabelIds.some((ids) => ids.includes(label.id)))
      .sort((a, b) =>
        (a.label || "")
          .toLowerCase()
          .localeCompare((b.label || "").toLowerCase())
      );

    const available = filtered
      .filter((label) => !issueLabelIds.some((ids) => ids.includes(label.id)))
      .sort((a, b) =>
        (a.label || "")
          .toLowerCase()
          .localeCompare((b.label || "").toLowerCase())
      );

    return { applied, available };
  }, [labels, labelSearch, issueLabelIds]);

  // Add global escape key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        document.body.style.pointerEvents = "";
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Cleanup pointer-events on unmount
      document.body.style.pointerEvents = "";
    };
  }, []);

  // Clean up pointer-events style when modal closes
  useEffect(() => {
    if (!showDeleteCanvasModal) {
      // Use requestAnimationFrame to ensure we run after React's updates
      const cleanup = requestAnimationFrame(() => {
        document.body.style.pointerEvents = "";
      });
      return () => cancelAnimationFrame(cleanup);
    }
  }, [showDeleteCanvasModal]);

  // Database and store connections
  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);
  const [searchParams] = useSearchParams();
  const currentProjectId = searchParams.get("project_id");

  // Redux state
  const project = useSelector((state: any) =>
    currentProjectId ? state.supabaseStore.projects?.[currentProjectId] : null
  );

  const {
    createIssueLabel,
    updateIssueProject,
    setSupabase,
    bulkUpdateIssueStatus,
    bulkUpdateIssuePriority,
    bulkUpdateIssueAssignee,
    bulkUpdateIssueEstimate,
    bulkUpdateIssueDueDate,
    bulkUpdateIssueLabel,
    bulkDeleteIssues,
  } = useIssuesStore();

  // Initialize Supabase in the store
  useEffect(() => {
    if (supabaseClient) {
      setSupabase(supabaseClient);
    }
  }, [supabaseClient, setSupabase]);

  // Event handlers
  const handleStatusChange = (statusId: string) => {
    if (!issueIds.length) return;
    const newStatus = status.find((s) => s.id === statusId);
    if (newStatus) {
      bulkUpdateIssueStatus(issueIds, newStatus.name);
      toast.success(
        `Status of ${issueIds.length} ${
          issueIds.length != 1 ? "issues" : "issue"
        } updated to ${newStatus.name}`
      );
    }
  };

  const handlePriorityChange = async (priority: number | undefined) => {
    if (!issueIds.length) return;

    try {
      await bulkUpdateIssuePriority(issueIds, priority ?? null);
      toast.success(
        priority === undefined
          ? `Priority removed from ${issueIds.length} ${
              issueIds.length != 1 ? "issues" : "issue"
            }`
          : `Priority of ${issueIds.length} ${
              issueIds.length != 1 ? "issues" : "issue"
            } set to ${getPriorityName(priority)}`
      );
    } catch (error) {
      console.error("Failed to update priority:", error);
      toast.error("Failed to update priority");
    }
  };

  const handleAssigneeChange = (userId: string | null) => {
    if (!issueIds.length) return;
    const newAssignee = userId
      ? users.find((u) => u.id === userId) || null
      : null;

    bulkUpdateIssueAssignee(issueIds, newAssignee);
    toast.success(
      newAssignee
        ? `Assigned to ${newAssignee.name || newAssignee.email || "Unknown"}`
        : `Unassigned ${issueIds.length} ${
            issueIds.length != 1 ? "issues" : "issue"
          }`
    );
  };

  const handleLabelToggle = async (labelId: string) => {
    if (!issueIds.length) return;

    let labelInterface: LabelInterface | null = null;
    if (labelId === null) {
      labelInterface = null;
    } else {
      const label: LabelInterface | undefined = (
        project?.labels as any[]
      )?.find((l) => l.id === labelId);

      if (label) {
        labelInterface = {
          id: label.id,
          label: label.name,
          color: label.color,
          name: label.name,
        };
      }
    }

    // now decide if we need to add or remove the label
    // get the canvases for each selected issue
    const issues = useIssuesStore
      .getState()
      .getAllIssues()
      .filter((issue) => issueIds.includes(issue.id));
    const issueLabels = issues.map((issue) => {
      const labelIds = issue.labels;
      if (Array.isArray(labelIds)) {
        return {
          issueId: issue.id,
          labels: labelIds,
        };
      } else {
        return {
          issueId: issue.id,
          labels: [],
        };
      }
    });

    if (labelInterface === null) {
      // set all issues to have no label
      issueLabels.forEach((entry) => {
        entry.labels = [];
      });

      const issueIdToLabelInterfaces: {
        issueId: string;
        labels: LabelInterface[];
      }[] = [];
      issueLabels.forEach((entry) => {
        issueIdToLabelInterfaces.push({ issueId: entry.issueId, labels: [] });
      });

      bulkUpdateIssueLabel(issueIdToLabelInterfaces);
      return;
    }

    // go through each entry in the issueLabels array
    let allIssuesHaveLabel = true;
    issueLabels.forEach((entry) => {
      const labels = entry.labels;
      let hasLabel = false;

      if (labelId === null && labels.length === 0) {
        hasLabel = true;
      } else {
        labels.forEach((label) => {
          if (label === labelId) {
            hasLabel = true;
          }
        });
      }

      if (!hasLabel) {
        allIssuesHaveLabel = false;
      }
    });

    let modifiedIssueLabels: { issueId: string; labels: string[] }[] = [];

    if (allIssuesHaveLabel) {
      // remove the label from the issues
      issueLabels.forEach((entry) => {
        const issueId = entry.issueId;
        const newLabels = [...entry.labels];
        newLabels.splice(newLabels.indexOf(labelInterface.id), 1);
        modifiedIssueLabels.push({ issueId: issueId, labels: newLabels });
      });
    } else {
      // add the label to the issues
      issueLabels.forEach((entry) => {
        const issueId = entry.issueId;
        const newLabels = [...entry.labels];
        // only add the label if it's not already in the list
        if (newLabels.indexOf(labelInterface.id) === -1) {
          newLabels.push(labelInterface.id);
        }
        modifiedIssueLabels.push({ issueId: issueId, labels: newLabels });
      });
    }

    // convert modifiedIssueLabels to issueIdToLabelInterfaces using the project labels
    let issueIdToLabelInterfaces: {
      issueId: string;
      labels: LabelInterface[];
    }[] = [];
    modifiedIssueLabels.forEach((entry) => {
      const labels = entry.labels;
      const labelInterfaces: LabelInterface[] = labels
        .map((label) => {
          const labelInterface = (project?.labels as any[])?.find(
            (l) => l.id === label
          );
          if (labelInterface) {
            return labelInterface;
          }
          return null;
        })
        .filter((label) => label !== null);
      issueIdToLabelInterfaces.push({
        issueId: entry.issueId,
        labels: labelInterfaces,
      });
    });

    bulkUpdateIssueLabel(issueIdToLabelInterfaces);
  };

  const handleSetDueDate = async (date: Date | null) => {
    if (!issueIds.length) {
      toast.error("Could not update due date");
      return;
    }

    try {
      await bulkUpdateIssueDueDate(issueIds, date?.toISOString() ?? null);
      if (date === null) {
        toast.success("Due date removed");
      } else if (isToday(date)) {
        toast.success("Due date set to Today");
      } else if (isTomorrow(date)) {
        toast.success("Due date set to Tomorrow");
      } else {
        toast.success(`Due date set to ${format(date, "MMM d")}`);
      }
    } catch (error) {
      console.error("Failed to update due date:", error);
      toast.error("Failed to update due date");
    }
  };

  const handleColorSelect = (color: any) => {
    // Create a new label with the selected color
    const newLabel: LabelInterface = {
      id: crypto.randomUUID(),
      name: newLabelName,
      color: color.value,
      label: newLabelName, // For backward compatibility
    };

    // Add the label to the issue
    createIssueLabel(newLabel);
    toast.success(`Created new label: ${newLabelName}`);

    // fetch the updated project
    if (!supabaseClient) return;
    // wait a second
    // retry 3 times with a delay of 1 second
    if (!issueIds.length) return;

    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        supabaseClient
          .from("projects")
          .select("*")
          .eq("id", useIssuesStore.getState().currentProject?.id ?? "")
          .single()
          .then(({ data, error }) => {
            if (error) {
            } else {
              const labels = data?.labels as any[];
              const label: LabelInterface = labels?.find(
                (l: any) => l.id === newLabel.id
              );
              if (label) {
                const issueIdToLabelInterfaces: {
                  issueId: string;
                  labels: LabelInterface[];
                }[] = [];
                issueIds.forEach((issueId) => {
                  const issue = useIssuesStore.getState().getIssueById(issueId);
                  const issueLabelIds = issue?.labels;
                  if (
                    issueLabelIds?.find((labelId) => labelId === newLabel.id)
                  ) {
                    return;
                  }
                  const issueLabelInterfaces = issueLabelIds?.map((labelId) => {
                    const label = labels?.find((l: any) => l.id === labelId);
                    return label;
                  });
                  if (issueLabelInterfaces) {
                    issueIdToLabelInterfaces.push({
                      issueId: issueId,
                      labels: [...issueLabelInterfaces, label],
                    });
                  }
                });
                bulkUpdateIssueLabel(issueIdToLabelInterfaces);
              } else {
                console.error("Label not found");
              }
            }
          });
      }, 1000);
    }
    // Reset the form and return to labels view
    setNewLabelName("");
    setLabelView("labels");
  };

  const handleSubscribe = () => {
    setIsSubscribed(!isSubscribed);
    toast.success(
      isSubscribed ? "Unsubscribed from issue" : "Subscribed to issue"
    );
  };

  const handleFavorite = () => {
    setIsFavorite(!isFavorite);
    toast.success(isFavorite ? "Removed from favorites" : "Added to favorites");
  };

  const { session } = useSession();

  const handleDeleteCanvas = async () => {
    if (!issueIds.length || !session) return;
    if (!issueIds.length) return;

    const token = await session.getToken();
    if (!token) {
      console.error("Failed to get session token");
      return;
    }

    await bulkDeleteIssues(issueIds, token);
    setShowDeleteCanvasModal(false);
    clearSelection();
  };

  // Handle opening the delete modal
  const handleOpenDeleteModal = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    // Force close any open menus
    document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
    // Then open the modal after a short delay
    setTimeout(() => {
      setShowDeleteCanvasModal(true);
      useProjectModalStore.getState().addProjectModal("delete-canvas-modal");
    }, 50);
  };

  const { clearSelection } = useIssueSelectionStore();

  // Handle the delete action
  const handleModalDelete = async () => {
    clearSelection();
    await handleDeleteCanvas();
    setShowDeleteCanvasModal(false);
  };

  const handleEstimateChange = async (points: number | undefined) => {
    try {
      await bulkUpdateIssueEstimate(issueIds, points ?? null);
      toast.success(
        points === undefined
          ? "Estimate removed"
          : `Estimate set to ${ESTIMATE_TO_TSHIRT_SIZE[points]}`
      );
    } catch (error) {
      console.error("Failed to update estimate:", error);
      toast.error("Failed to update estimate");
    }
  };

  const filteredEstimates = ESTIMATE_POINTS.filter((points) =>
    points.toString().includes(estimateSearch)
  );

  // Add scrollbar styles
  const scrollbarStyles = `
    .context-menu-scroll::-webkit-scrollbar {
      width: 8px;
    }
    .context-menu-scroll::-webkit-scrollbar-track {
      background: transparent;
    }
    .context-menu-scroll::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.3);
      border-radius: 4px;
    }
    .context-menu-scroll::-webkit-scrollbar-thumb:hover {
      background-color: rgba(255, 255, 255, 0.5);
    }
  `;

  const filteredPriorities = priorities.filter((priority) =>
    priority.name?.toLowerCase().includes(prioritySearch.toLowerCase())
  );

  const filteredStatuses = status.filter((s) =>
    s.name?.toLowerCase().includes(statusSearch.toLowerCase())
  );

  const filteredAssignees = useMemo(() => {
    return users.filter((user) => {
      const searchTerm = assigneeSearch.toLowerCase();
      const nameMatch = user.name?.toLowerCase().includes(searchTerm);
      const emailMatch = user.email?.toLowerCase().includes(searchTerm);
      return nameMatch || emailMatch;
    });
  }, [users, assigneeSearch]);

  return (
    <>
      <style>{scrollbarStyles}</style>
      <ContextMenuPortal>
        <ShortcutHandler enabled={true}>
          <ContextMenuContent
            className="w-64"
            onCloseAutoFocus={(e) => {
              e.preventDefault();
              setLabelView("labels");
              setNewLabelName("");
              setLabelSearch("");
              setEstimateSearch("");
              setPrioritySearch("");
              setStatusSearch("");
              setAssigneeSearch("");
              useProjectModalStore
                .getState()
                .removeProjectModal("issue-context-menu");
            }}
          >
            <ContextMenuGroup>
              <ContextMenuSub>
                <ContextMenuSubTrigger>
                  <CircleCheck className="mr-2 size-4" /> Status
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-48">
                  <div className="px-2 py-1.5">
                    <Input
                      ref={statusInputRef}
                      placeholder="Search statuses..."
                      value={statusSearch}
                      onChange={(e) => setStatusSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      onKeyUp={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="h-7 text-sm bg-secondary-n-950 border-0"
                    />
                  </div>
                  <ContextMenuSeparator />
                  <div
                    className="max-h-[256px] overflow-y-auto context-menu-scroll"
                    style={{
                      scrollbarWidth: "thin",
                      scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
                    }}
                  >
                    <div className="px-2 py-1 text-2xs font-medium text-secondary-n-400">
                      Statuses
                    </div>
                    {filteredStatuses.map((s) => {
                      const Icon = s.icon;
                      return (
                        <ContextMenuItem
                          key={s.id}
                          onClick={() => handleStatusChange(s.id)}
                          className="flex items-center justify-between"
                        >
                          <div className="flex items-center gap-1.5">
                            {s.name === "Backlog" ? (
                              <div className="pl-[3px]">
                                <CircleDashed className="size-3.5" />
                              </div>
                            ) : (
                              <Icon />
                            )}{" "}
                            {s.name}
                          </div>
                          <div className="flex items-center gap-1.5">
                            {getAppliedStatusIconForIssues(
                              issueIds,
                              s.name,
                              (issue, statusName) =>
                                issue.status?.name === statusName
                            )}
                          </div>
                        </ContextMenuItem>
                      );
                    })}
                  </div>
                </ContextMenuSubContent>
              </ContextMenuSub>

              <ContextMenuSub>
                <ContextMenuSubTrigger>
                  <NoPriorityIcon className="mr-2 size-4" /> Priority
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-48">
                  <div className="px-2 py-1.5">
                    <Input
                      ref={priorityInputRef}
                      placeholder="Search priorities..."
                      value={prioritySearch}
                      onChange={(e) => setPrioritySearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      onKeyUp={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="h-7 text-sm bg-secondary-n-950 border-0"
                    />
                  </div>
                  <ContextMenuSeparator />
                  <div
                    className="max-h-[200px] overflow-y-auto overflow-x-hidden context-menu-scroll"
                    style={{
                      scrollbarWidth: "thin",
                      scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
                    }}
                  >
                    <ContextMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePriorityChange(undefined);
                      }}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <NoPriorityIcon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>No priority</span>
                        </div>
                        {getAppliedStatusIconForIssues(
                          issueIds,
                          null,
                          (issue, priorityId) =>
                            issue.priority === parseInt(priorityId ?? "0")
                        )}
                      </div>
                    </ContextMenuItem>

                    <ContextMenuSeparator className="w-full" />

                    <div className="px-2 py-1 text-2xs font-medium text-secondary-n-400">
                      Priorities
                    </div>

                    {filteredPriorities.map((option) => (
                      <ContextMenuItem
                        key={option.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePriorityChange(option.id);
                        }}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <option.icon className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{option.name}</span>
                          </div>
                          {getAppliedStatusIconForIssues(
                            issueIds,
                            option.id.toString(),
                            (issue, priorityId) =>
                              issue.priority === parseInt(priorityId ?? "0")
                          )}
                        </div>
                      </ContextMenuItem>
                    ))}
                  </div>
                </ContextMenuSubContent>
              </ContextMenuSub>

              <ContextMenuSub>
                <ContextMenuSubTrigger>
                  <HalfFilledTriangleIcon className="mr-2 size-4" /> Estimate
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-48">
                  <div className="px-2 py-1.5">
                    <Input
                      ref={estimateInputRef}
                      placeholder="Search estimates..."
                      value={estimateSearch}
                      onChange={(e) => setEstimateSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      onKeyUp={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="h-7 text-sm bg-secondary-n-950 border-0"
                    />
                  </div>
                  <ContextMenuSeparator />
                  <div className="overflow-x-hidden">
                    <ContextMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEstimateChange(undefined);
                      }}
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <TriangleDashed className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>No estimate</span>
                        </div>
                        {getAppliedStatusIconForIssues(
                          issueIds,
                          null,
                          (issue, estimate) =>
                            issue.estimate ===
                            (estimate ? parseInt(estimate) : null)
                        )}
                      </div>
                    </ContextMenuItem>

                    <ContextMenuSeparator className="w-full" />

                    <div className="px-2 py-1 text-2xs font-medium text-secondary-n-400">
                      Estimates
                    </div>

                    {filteredEstimates.map((points) => (
                      <ContextMenuItem
                        key={points}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEstimateChange(points);
                        }}
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <HalfFilledTriangleIcon className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{ESTIMATE_TO_TSHIRT_SIZE[points]}</span>
                          </div>
                          {getAppliedStatusIconForIssues(
                            issueIds,
                            points.toString(),
                            (issue, estimate) =>
                              issue.estimate === parseInt(estimate ?? "0")
                          )}
                        </div>
                      </ContextMenuItem>
                    ))}
                  </div>
                </ContextMenuSubContent>
              </ContextMenuSub>

              <ContextMenuSub>
                <ContextMenuSubTrigger>
                  <UserIcon className="mr-2 size-4" /> Assignee
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-48">
                  <div className="px-2 py-1.5">
                    <Input
                      ref={assigneeInputRef}
                      placeholder="Search assignees..."
                      value={assigneeSearch}
                      onChange={(e) => setAssigneeSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      onKeyUp={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      className="h-7 text-sm bg-secondary-n-950 border-0"
                    />
                  </div>
                  <ContextMenuSeparator />
                  <div
                    className="max-h-[256px] overflow-y-auto overflow-x-hidden context-menu-scroll"
                    style={{
                      scrollbarWidth: "thin",
                      scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
                    }}
                  >
                    {/* No Assignee Option */}
                    <ContextMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAssigneeChange(null);
                      }}
                      className="flex items-center w-full py-[4px] pl-2"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <CircleUserRound className="size-5 text-secondary-n-600" />
                          </div>
                          <span>No assignee</span>
                        </div>
                        {getAppliedStatusIconForIssues(
                          issueIds,
                          null,
                          (issue, assigneeId) =>
                            (issue?.assignee?.id ?? null) === assigneeId
                        )}
                      </div>
                    </ContextMenuItem>

                    {/* Current Assignee Section */}
                    {issues.some((issue) => issue?.assignee) && (
                      <>
                        <ContextMenuSeparator />
                        {/* Get unique assignees */}
                        {Array.from(
                          new Set(
                            issues
                              .map((issue) => issue?.assignee)
                              .filter(Boolean)
                              .map((assignee) => assignee?.id)
                          )
                        ).map((assigneeId) => {
                          // First try to find the assignee in the users list
                          const assignee =
                            users.find((u) => u.id === assigneeId) ||
                            // Fallback to the assignee from the issue if not in users list
                            issues.find(
                              (issue) => issue?.assignee?.id === assigneeId
                            )?.assignee;

                          if (!assignee) return null;

                          // Only show if matches search
                          const searchTerm = assigneeSearch.toLowerCase();
                          const nameMatch = assignee.name
                            ?.toLowerCase()
                            .includes(searchTerm);
                          const emailMatch = assignee.email
                            ?.toLowerCase()
                            .includes(searchTerm);
                          if (searchTerm && !nameMatch && !emailMatch)
                            return null;

                          return (
                            <ContextMenuItem
                              key={assignee.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssigneeChange(assignee.id);
                              }}
                              className="flex items-center w-full py-[4px] pl-2"
                            >
                              <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 flex items-center justify-center">
                                    {assignee.avatarUrl ? (
                                      <img
                                        className="w-[20px] h-[20px] rounded-md"
                                        src={assignee.avatarUrl}
                                        crossOrigin="anonymous"
                                      />
                                    ) : (
                                      <Avatar className="h-5 w-5">
                                        <AvatarFallback>
                                          {
                                            (assignee.name ||
                                              assignee.email ||
                                              "")[0]
                                          }
                                        </AvatarFallback>
                                      </Avatar>
                                    )}
                                  </div>
                                  <span className="truncate max-w-[100px]">
                                    {assignee.name || assignee.email}
                                  </span>
                                </div>
                                {getAppliedStatusIconForIssues(
                                  issueIds,
                                  assignee.id,
                                  (issue, assigneeId) =>
                                    issue.assignee?.id === assigneeId
                                )}
                              </div>
                            </ContextMenuItem>
                          );
                        })}
                      </>
                    )}

                    {/* Team Members Section */}
                    {filteredAssignees.filter(
                      (user) =>
                        !issues.some((issue) => issue?.assignee?.id === user.id)
                    ).length > 0 && (
                      <>
                        <ContextMenuSeparator />
                        <div className="px-2 py-1 text-2xs font-medium text-secondary-n-400">
                          Team members
                        </div>
                        {filteredAssignees
                          .filter(
                            (user) =>
                              !issues.some(
                                (issue) => issue?.assignee?.id === user.id
                              )
                          )
                          .map((user) => (
                            <ContextMenuItem
                              key={user.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssigneeChange(user.id);
                              }}
                              className="flex items-center w-full py-[4px] pl-2"
                            >
                              <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 flex items-center justify-center">
                                    {user.avatarUrl ? (
                                      <img
                                        className="w-[20px] h-[20px] rounded-md"
                                        src={user.avatarUrl}
                                        crossOrigin="anonymous"
                                      />
                                    ) : (
                                      <Avatar className="h-5 w-5">
                                        <AvatarFallback>
                                          {(user.name || user.email || "")[0]}
                                        </AvatarFallback>
                                      </Avatar>
                                    )}
                                  </div>
                                  <span className="truncate max-w-[100px]">
                                    {user.name || user.email}
                                  </span>
                                </div>
                                {getAppliedStatusIconForIssues(
                                  issueIds,
                                  user.id,
                                  (issue, assigneeId) =>
                                    issue.assignee?.id === assigneeId
                                )}
                              </div>
                            </ContextMenuItem>
                          ))}
                      </>
                    )}
                  </div>
                </ContextMenuSubContent>
              </ContextMenuSub>

              <ContextMenuSub>
                <ContextMenuSubTrigger>
                  <Tag className="mr-2 size-4" /> Labels
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-48">
                  {labelView === "labels" && (
                    <>
                      <div className="px-2 py-1.5">
                        <Input
                          ref={searchInputRef}
                          placeholder="Search labels..."
                          value={labelSearch}
                          onChange={(e) => setLabelSearch(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()}
                          onKeyUp={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onBlur={(e) => {
                            e.preventDefault();
                            searchInputRef.current?.focus();
                          }}
                          className="h-7 text-sm bg-secondary-n-950 border-0"
                        />
                      </div>
                      <ContextMenuSeparator />
                      <div
                        className="max-h-[200px] overflow-y-auto overflow-x-hidden context-menu-scroll"
                        style={{
                          scrollbarWidth: "thin",
                          scrollbarColor:
                            "rgba(255, 255, 255, 0.3) transparent",
                        }}
                      >
                        {sortedAndFilteredLabels.applied.length > 0 && (
                          <>
                            {sortedAndFilteredLabels.applied.map((label) => (
                              <ContextMenuItem
                                key={label.id}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleLabelToggle(label.id);
                                }}
                                className="flex items-center hover:bg-secondary-n-800"
                              >
                                <div className="flex items-center justify-between w-full py-0 px-2 min-w-0">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div
                                      className="size-3.5 rounded-[3px] flex-shrink-0"
                                      style={{
                                        backgroundColor:
                                          label.color?.value || label.color,
                                      }}
                                    />
                                    <span className="truncate">
                                      {label.label}
                                    </span>
                                  </div>
                                  {getAppliedStatusIconForIssues(
                                    issueIds,
                                    label.id,
                                    (issue, labelId) =>
                                      Array.isArray(issue.labels) &&
                                      issue.labels.includes(labelId ?? "")
                                  )}
                                </div>
                              </ContextMenuItem>
                            ))}
                          </>
                        )}

                        {sortedAndFilteredLabels.available.length > 0 && (
                          <>
                            {sortedAndFilteredLabels.applied.length > 0 && (
                              <>
                                <ContextMenuSeparator />
                                <div className="text-2xs font-medium text-secondary-n-400 ml-2 py-1">
                                  Labels
                                </div>
                              </>
                            )}
                            {sortedAndFilteredLabels.available.map((label) => (
                              <ContextMenuItem
                                key={label.id}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleLabelToggle(label.id);
                                }}
                                className="flex items-center hover:bg-secondary-n-800"
                              >
                                <div className="flex items-center justify-between w-full py-0 px-2 min-w-0">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <div
                                      className="size-3.5 rounded-[3px] flex-shrink-0"
                                      style={{
                                        backgroundColor:
                                          label.color?.value || label.color,
                                      }}
                                    />
                                    <span className="truncate">
                                      {label.label}
                                    </span>
                                  </div>
                                </div>
                              </ContextMenuItem>
                            ))}
                          </>
                        )}

                        {sortedAndFilteredLabels.applied.length === 0 &&
                          sortedAndFilteredLabels.available.length === 0 && (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                              No labels found
                            </div>
                          )}
                      </div>

                      <ContextMenuSeparator />

                      <ContextMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          setLabelView("new");
                          setLabelSearch("");
                        }}
                        className="flex items-center justify-center gap-2 text-sm cursor-pointer bg-background-CTA hover:!brightness-110 text-white m-1 mt-2 rounded-md"
                      >
                        <Plus className="size-4" />
                        Create new label
                      </ContextMenuItem>
                    </>
                  )}
                  {labelView === "new" && (
                    <>
                      <div className="px-2 py-1.5">
                        <Input
                          ref={labelInputRef}
                          onKeyDown={(e) => e.stopPropagation()}
                          onKeyUp={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setNewLabelName(e.target.value)
                          }
                          className="h-7 text-sm bg-secondary-n-950 border-0"
                          placeholder="Label name..."
                          onBlur={(e) => {
                            // Prevent losing focus
                            e.preventDefault();
                            labelInputRef.current?.focus();
                          }}
                        />
                      </div>
                      <ContextMenuSeparator />
                      <ContextMenuItem
                        disabled={!newLabelName?.trim()}
                        onSelect={(e) => {
                          e.preventDefault();
                          setLabelView("colorSelect");
                        }}
                        className="flex justify-start gap-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                      >
                        <Plus className="size-4" />
                        <span className="overflow-hidden break-words">
                          Add new label: {newLabelName}
                        </span>
                      </ContextMenuItem>
                      <ContextMenuItem
                        className="flex justify-between gap-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                        onSelect={(e) => {
                          e.preventDefault();
                          setLabelView("labels");
                          setNewLabelName("");
                        }}
                      >
                        Back
                        <ChevronLeft className="size-4" />
                      </ContextMenuItem>
                    </>
                  )}
                  {labelView === "colorSelect" && (
                    <>
                      <ContextMenuItem
                        disabled
                        className="overflow-hidden break-words"
                      >
                        Pick a color for label: {newLabelName}
                      </ContextMenuItem>
                      {COLOR_LIST.map((color) => (
                        <ContextMenuCheckboxItem
                          key={color.key}
                          onSelect={(e) => {
                            e.preventDefault();
                            handleColorSelect(color);
                          }}
                          className="flex gap-2 items-center text-sm cursor-pointer"
                        >
                          <div
                            className="size-2.5 rounded-full"
                            style={{ backgroundColor: color.value }}
                          />
                          <span>{color.label}</span>
                        </ContextMenuCheckboxItem>
                      ))}
                    </>
                  )}
                </ContextMenuSubContent>
              </ContextMenuSub>

              <ContextMenuSub>
                <ContextMenuSubTrigger className="flex items-center gap-2">
                  <CalendarClock className="size-4" />
                  <span>Due Date</span>
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className="w-auto p-1.5">
                  <ContextMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetDueDate(null);
                    }}
                    className="px-2 py-1.5"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <ClockFading className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>No due date</span>
                      </div>
                      {getAppliedStatusIconForIssues(
                        issueIds,
                        null,
                        (issue, dueDate) => issue.dueDate === dueDate
                      )}
                    </div>
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                  <div className="p-0">
                    <Calendar
                      mode="single"
                      selected={
                        issues?.length &&
                        issues[0]?.dueDate &&
                        issues[0]?.dueDate !== ""
                          ? new Date(issues[0]?.dueDate)
                          : undefined
                      }
                      onSelect={(date) => handleSetDueDate(date || null)}
                      initialFocus
                    />
                  </div>
                </ContextMenuSubContent>
              </ContextMenuSub>
              <ContextMenuSeparator />
              <ContextMenuItem
                className="flex items-center gap-2 text-error-500 hover:bg-error-500/10"
                onSelect={handleOpenDeleteModal}
              >
                <Trash2 className="size-4" />
                Delete
              </ContextMenuItem>
            </ContextMenuGroup>
          </ContextMenuContent>
        </ShortcutHandler>
      </ContextMenuPortal>
      <AlertDialog
        open={showDeleteCanvasModal}
        onOpenChange={(open) => {
          setShowDeleteCanvasModal(open);
          if (!open) {
            useProjectModalStore
              .getState()
              .removeProjectModal("delete-canvas-modal");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            {issueIds.length > 1 ? (
              <AlertDialogTitle>
                Delete {issueIds.length} Canvases
              </AlertDialogTitle>
            ) : (
              <AlertDialogTitle>Delete Canvas</AlertDialogTitle>
            )}
            <AlertDialogDescription>
              {issueIds.length > 1
                ? "Are you sure you want to delete these canvases? This action cannot be undone."
                : "Are you sure you want to delete this canvas? This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-error-600 text-white hover:bg-error-500"
              onClick={handleModalDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
